import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useTheme } from "../common/ThemeContext";
import { fetchAuth } from "../common/api";
import { formatLocalDate } from "../common/datetimeUtils";

const SectionCard = ({ title, right, children }) => {
  const { isDarkMode } = useTheme();
  return (
    <section
      className={`rounded-2xl p-6 shadow-lg border ${
        isDarkMode ? "bg-gray-800 border-gray-700" : "bg-white border-gray-200"
      }`}
    >
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-semibold">{title}</h2>
        {right}
      </div>
      {children}
    </section>
  );
};

SectionCard.propTypes = {
  title: PropTypes.string.isRequired,
  right: PropTypes.node,
  children: PropTypes.node,
};

export default function NotesTab({ playerId }) {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();

  const [injuries, setInjuries] = useState([]);
  const [playerNote, setPlayerNote] = useState("");   // ← note unique affichée/éditée
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [sortNotesFirst, setSortNotesFirst] = useState(true);

  // --- API ---
  const listInjuries = async (pid) => {
    const res = await fetchAuth(`/players/${pid}/injuries`);
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Error list_injuries");
    return data;
  };

  const patchInjuryNote = async (injuryId, note) => {
    const res = await fetchAuth(`/injuries/${injuryId}`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || "Error patch_note");
    return data;
  };

  // --- Helpers ---
  const primaryInjury = useMemo(() => {
    if (!injuries.length) return null;
    const sorted = [...injuries].sort((a, b) => {
      const da = Number.isFinite(a.duration_weeks) ? a.duration_weeks : 0;
      const db = Number.isFinite(b.duration_weeks) ? b.duration_weeks : 0;
      if (db !== da) return db - da;
      const ta = a.injury_date ? new Date(a.injury_date).getTime() : 0;
      const tb = b.injury_date ? new Date(b.injury_date).getTime() : 0;
      return tb - ta; // plus récent d’abord si égalité
    });
    return sorted[0] || null;
  }, [injuries]);

  const visibleInjuries = useMemo(() => {
    let list = [...injuries];
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter((inj) =>
        `${inj.region} ${inj.type} ${inj.side} ${inj.note || ""}`.toLowerCase().includes(q)
      );
    }
    if (sortNotesFirst) {
      list.sort((a, b) => {
        const an = (a.note ?? "").trim().length > 0 ? 1 : 0;
        const bn = (b.note ?? "").trim().length > 0 ? 1 : 0;
        return bn - an;
      });
    }
    return list;
  }, [injuries, sortNotesFirst, search]);

  // --- Load ---
  useEffect(() => {
    (async () => {
      try {
        const list = await listInjuries(playerId);
        setInjuries(list);
        // Alimente la note unique depuis l’injury primaire
        const primary = list.length
          ? [...list].sort((a, b) => {
              const da = Number.isFinite(a.duration_weeks) ? a.duration_weeks : 0;
              const db = Number.isFinite(b.duration_weeks) ? b.duration_weeks : 0;
              if (db !== da) return db - da;
              const ta = a.injury_date ? new Date(a.injury_date).getTime() : 0;
              const tb = b.injury_date ? new Date(b.injury_date).getTime() : 0;
              return tb - ta;
            })[0]
          : null;
        setPlayerNote((primary?.note ?? "").trim());
      } catch (e) {
      
      }
    })();
  }, [playerId]);

  // --- Save unique note ---
  const savePlayerNote = async () => {
    if (!primaryInjury) return; // rien à sauvegarder s’il n’y a pas de blessure
    setSaving(true);
    try {
      await patchInjuryNote(primaryInjury.id, playerNote ?? "");
      // recharge pour refléter que seule la primaire a la note (les autres -> NULL)
      const refreshed = await listInjuries(playerId);
      setInjuries(refreshed);
    } catch (e) {
      
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-8">
      {/* --------- ÉDITEUR DE NOTE UNIQUE --------- */}
      <SectionCard
        title={t("staff_update.physio_note", { defaultValue: "Physio note" })}
      >
        <div className={`rounded-2xl border p-4 ${isDarkMode ? "border-gray-700 bg-gray-800/60" : "border-gray-200 bg-white"}`}>
          {/* <div className="mb-2 text-sm opacity-80">
            La note du joueur est stockée sur <strong>une seule blessure</strong> : la blessure
            « principale » (durée la plus longue, puis la plus récente). Les autres blessures restent sans note.
          </div> */}

          <textarea
            className={`w-full rounded-xl px-4 py-3 text-[0.95rem] leading-relaxed border resize-y min-h-[110px] focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
              isDarkMode ? "bg-gray-900 border-gray-700" : "bg-white border-gray-300"
            }`}
            placeholder={t("staff_update.physio_note_placeholder", { defaultValue: "E.g. pain 4/10, gentle stretching, check in 48h, etc." })}
            value={playerNote}
            onChange={(e) => setPlayerNote(e.target.value)}
          />

          <div className="mt-3 flex items-center justify-between text-xs opacity-70">
            <div>
              {primaryInjury ? (
                <>
                  {t("staff_update.note_attached_to", { defaultValue: "Note attached to" })}&nbsp;
                  <span className="font-semibold">
                    {formatLocalDate(primaryInjury.injury_date)} • {primaryInjury.region} • {primaryInjury.type}
                  </span>
                </>
              ) : (
                <span>{t("staff_update.no_injury_attach", { defaultValue: "No injury - cannot attach note." })}</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPlayerNote(primaryInjury?.note ?? "")}
                className={`px-3 py-1.5 rounded-lg font-medium ${
                  isDarkMode ? "bg-gray-700 hover:bg-gray-600 text-gray-100" : "bg-gray-100 hover:bg-gray-200 text-gray-700"
                }`}
              >
                {t("staff_update.reset", { defaultValue: "Reset" })}
              </button>
              <button
                onClick={savePlayerNote}
                disabled={!primaryInjury || saving}
                className={`px-4 py-1.5 rounded-lg font-semibold text-white ${
                  isDarkMode ? "bg-indigo-600 hover:bg-indigo-700" : "bg-indigo-600 hover:bg-indigo-700"
                } ${(!primaryInjury || saving) ? "opacity-70 cursor-not-allowed" : ""}`}
              >
                {saving
                  ? t("staff_update.saving", { defaultValue: "Saving..." })
                  : t("staff_update.save_note", { defaultValue: "Save note" })}
              </button>
            </div>
          </div>
        </div>
      </SectionCard>

      {/* --------- LISTE DES BLESSURES (badge de la note) --------- */}
      <SectionCard title={t("staff_update.injuries", { defaultValue: "Injuries" })}>
        {visibleInjuries.length ? (
          <ul className="space-y-4">
            {visibleInjuries.map((inj) => {
              const isPrimary = primaryInjury && inj.id === primaryInjury.id;
              const hasNote = (inj.note ?? "").trim().length > 0;

              return (
                <li key={inj.id} className={`rounded-2xl border p-4 ${isDarkMode ? "border-gray-700 bg-gray-800/50" : "border-gray-200 bg-white"}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div className="space-y-1">
                      <div className="text-sm font-semibold">
                        {formatLocalDate(inj.injury_date)} • {inj.region} • {inj.type}
                      </div>
                      <div className="text-xs opacity-70">Side: {inj.side} — Duration: {inj.duration_weeks}w</div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isPrimary && (
                        <span className="text-xs px-2 py-1 rounded-full bg-indigo-600 text-white">
                          {t("staff_update.primary_note_lives_here", { defaultValue: "primary (note lives here)" })}
                        </span>
                      )}
                      {/* <span className={`text-xs px-2 py-1 rounded-full ${hasNote ? "bg-green-600 text-white" : "bg-gray-500 text-white"}`}>
                        {hasNote ? "has note" : "no note"}
                      </span> */}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-gray-500">{t("staff_update.no_injuries", { defaultValue: "No injuries." })}</p>
        )}
      </SectionCard>
    </div>
  );
}

NotesTab.propTypes = {
  playerId: PropTypes.string.isRequired,
};
